import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { Toast } from '@m/alchemy-ui/Toast';
import { CommunityContext } from '../../../context/Community';
import { type IdeaRow, MODERATION_STATUS } from '../IdeaCard.types';
import { getCommonCardFlags } from '../IdeaCard.helpers';
import {
  FlaggedIndicator,
  IdeaAuthor,
  IdeaBody,
  IdeaFooter,
  IdeaLabels,
  IdeaThumbnail,
  IdeaTitle,
  VoteActions
} from '../components';
import { useIdeaVoteState } from '../hooks/useIdeaVoteState';
import * as Styled from '../components/IdeaCard.styled';

interface StandardIdeaCardProps {
  readonly idea: IdeaRow;
  readonly isOwnProfile: boolean;
}

export const StandardIdeaCard = ({ idea, isOwnProfile }: StandardIdeaCardProps) => {
  const community = useContext(CommunityContext);
  const challenge = idea.challenge;

  const { voteCount, score, toastMessages, portalTarget, portalTargetRef, handleVoteSuccess, handleVoteError } =
    useIdeaVoteState(idea.voteCount, idea.score);

  const { isAnonymous, hasAccess, canModerate, isFlagged, isArchived, isPrivate, isHidden } = getCommonCardFlags(
    idea,
    community.hideUnModerated === 1
  );

  const isClosed = challenge?.access?.options?.showClosedEnabled ?? false;
  const votingEnabled = challenge?.access?.options?.voteOnIdeasEnabled ?? false;
  const walletEmpty =
    challenge?.voting?.wallet?.isEnabled && idea.viewerWalletAllowance === 0 && idea.viewerVoteDirection === null;
  const hideVoteButtons = isArchived || isClosed;
  const isVotingDisabled =
    isOwnProfile ||
    hideVoteButtons ||
    walletEmpty ||
    !votingEnabled ||
    idea.moderationStatus === MODERATION_STATUS.NOT_APPROVED;

  return (
    <>
      <Styled.CardWrapper>
        {isFlagged && (isOwnProfile || canModerate) && <FlaggedIndicator />}

        {idea.imageFile && <IdeaThumbnail imageFile={idea.imageFile} ideaId={idea.id} hasAccess={hasAccess} />}

        <Styled.ContentWrapper>
          <IdeaLabels
            moderationStatus={idea.moderationStatus}
            statusLabels={idea.statusLabels}
            isHidden={isHidden}
            isPrivate={isPrivate}
          />

          <IdeaTitle ideaId={idea.id} title={idea.title} hasAccess={hasAccess} />
          <IdeaBody ideaId={idea.id} text={idea.text} hasAccess={hasAccess} />

          <IdeaFooter
            voteCount={voteCount}
            score={score}
            commentCount={idea.commentCount ?? 0}
            votingType={challenge?.voting?.type}
            isBlindVoting={challenge?.voting?.isBlind ?? false}
            canModerate={canModerate}
            maxStars={challenge?.voting?.options?.maxStars ?? undefined}
            suffix={
              <>
                {hideVoteButtons && (
                  <Styled.VotingClosed>
                    <FormattedMessage id="votingClosed" defaultMessage="Voting closed" />
                  </Styled.VotingClosed>
                )}
                {!isOwnProfile && hasAccess && !hideVoteButtons && (
                  <VoteActions
                    ideaId={idea.id}
                    viewerVoteDirection={idea.viewerVoteDirection}
                    votingType={challenge?.voting?.type}
                    votingOptions={challenge?.voting?.options}
                    disabled={isVotingDisabled}
                    onVoteSuccess={handleVoteSuccess}
                    onVoteError={handleVoteError}
                  />
                )}
              </>
            }
          />
        </Styled.ContentWrapper>

        <IdeaAuthor ideaId={idea.id} user={idea.user} isAnonymous={isAnonymous} createdAt={idea.createdAt} />
      </Styled.CardWrapper>
      <div ref={portalTargetRef} style={{ position: 'relative' }}>
        {/* @ts-expect-error - Toast portalTarget typing mismatch */}
        <Toast messages={toastMessages} portalTarget={portalTarget} />
      </div>
    </>
  );
};
